import * as React from 'react';
import { useIntl } from 'react-intl';
import { useQueries } from '@tanstack/react-query';
import {
  CmdLabel,
  CmdSimpleSelectOption,
  CmdSubpage,
  CmdSubpageButton,
} from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { useRecoilValue } from 'recoil';
import { useForm } from 'react-hook-form';
import * as EmployeeService from './EmployeeService';
import OnlineHelp from '../OnlineHelp';
import { IEmployeeData } from './types';
import { customersAtom, ICustomer, selectedCustomersAtom } from '../../store/recoil/customers';
import { getById } from '../Customers/CustomerService';
import { IS_DRIVER, IS_USER } from './EmployeeService';
import useAnalytics from '../../hooks/useAnalytics';

interface DetailProps {
  closeCallback: () => void;
  id: number;
}

interface IEmployeeInputFormDetail {
  customerId: string | number | null;
  title: string;
  firstName: string;
  lastName: string;
  identCards: string[];
  personalNumber: string;
  workDepartmentId: string | null;
  costCenterId: string | null;
  workingHoursFrom: Date;
  workingHoursTo: Date;
  email: string;
  phone: string;
  addressStreet: string;
  addressCity: string;
  addressPostal: string;
  types: string[];
  username: string;
  roles: string[];
  vehicles: string[];
  drivers: string[];
  twoFactorAuth: string[];
  roleIsRequired: boolean;
  status: string;
  workingHours: string;
}

function Detail(props: DetailProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  // const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const customers = useRecoilValue<ICustomer[]>(customersAtom);
  const [idCardOptions, setIdCardOptions] = React.useState<CmdSimpleSelectOption[]>([]);
  const [workDepartmentOptions, setWorkDepartmentOptions] = React.useState<CmdSimpleSelectOption[]>(
    []
  );
  const [costCenterOptions, setCostCenterOptions] = React.useState<CmdSimpleSelectOption[]>([]);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const { trackEvent } = useAnalytics();

  const {
    setValue,
    watch,
  } = useForm<IEmployeeInputFormDetail>({
    mode: 'onChange',
    defaultValues: {
      customerId: null,
      title: '',
      firstName: '',
      lastName: '',
      identCards: [],
      personalNumber: '',
      workDepartmentId: null,
      costCenterId: null,
      workingHours: '',
      email: '',
      phone: '',
      addressStreet: '',
      addressCity: '',
      addressPostal: '',
      types: [],
      roleIsRequired: false,
      status: '',
      twoFactorAuth: [],
    },
  });

  const closeSubpage = () => {
    props.closeCallback();
    trackEvent(['employee form', 'click', 'Close employee read form'])
  };

  const subpageButtons: CmdSubpageButton[] = [
    {
      id: 'employee-form-close',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'form.close' }),
      closeCallback: closeSubpage,
    },
  ];

  const results = useQueries({
    queries: [
      {
        queryKey: ['employee', props.id],
        queryFn: async () => {
          const employeeResponse = await EmployeeService.get(props.id);

          if (employeeResponse) {
            const types: string[] = [];
            if (employeeResponse.isUser) {
              types.push(IS_USER);
            }
            if (employeeResponse.isDriver) {
              types.push(IS_DRIVER);
            }
            const assignedIdentCardsValues = employeeResponse.assignedIdentCards
              ? employeeResponse.assignedIdentCards.map((i: string | number) => String(i))
              : [];
            setValue('customerId', employeeResponse.customerId);
            setValue('title', employeeResponse.title || '');
            setValue('firstName', employeeResponse.firstName || '');
            setValue('lastName', employeeResponse.lastName || '');
            setValue('identCards', assignedIdentCardsValues);
            setValue('personalNumber', employeeResponse.personalNumber || '');
            setValue(
              'workDepartmentId',
              employeeResponse.workDepartmentId ? String(employeeResponse.workDepartmentId) : ''
            );
            setValue(
              'costCenterId',
              employeeResponse.costCenterId ? String(employeeResponse.costCenterId) : ''
            );

            setValue('workingHours', employeeResponse.workingHours || '');
            setValue('email', employeeResponse.email || '');
            setValue('phone', employeeResponse.phone || '');
            setValue('addressStreet', employeeResponse.addressStreet || '');
            setValue('addressCity', employeeResponse.addressCity || '');
            setValue('addressPostal', employeeResponse.addressPostal || '');
            setValue('types', types);
          }

          return employeeResponse;
        },
      },
      {
        queryKey: ['idCardOptions', watch('customerId')],
        queryFn: async () => {
          if (watch('customerId')) {
            const response = await EmployeeService.getIdentificationCards(
              Number(watch('customerId')),
              props.id
            );
            if (response) {
              setIdCardOptions(response);
            }
            return response;
          }
          return [];
        },
      },
      {
        queryKey: ['workDepartmentOptions', watch('customerId')],
        queryFn: async () => {
          if (watch('customerId')) {
            const response = await EmployeeService.getWorkDepartments(Number(watch('customerId')));
            if (response) {
              setWorkDepartmentOptions(response);
            }
            return response;
          }
          return [];
        },
      },
      {
        queryKey: ['costCenterOptions', watch('customerId')],
        queryFn: async () => {
          if (watch('customerId')) {
            const response = await EmployeeService.getCostCenters(Number(watch('customerId')));
            if (response) {
              setCostCenterOptions(response);
            }
            return response;
          }
          return [];
        },
      },
      {
        queryKey: ['rolesOptions', watch('username')],
        queryFn: async () => {
          if (watch('username')) {
            const values = {
              customerId: watch('customerId'),
              username: watch('username'),
              isUser: watch('types').includes(IS_USER),
              isDriver: watch('types').includes(IS_DRIVER),
              id: props.id,
            };

            const response = await EmployeeService.getRoles(values);
            return response;
          }
          return [];
        },
      },
      {
        queryKey: ['driversOptions', watch('roles')],
        queryFn: async () => {
          if (watch('username')) {
            const values = {
              customerId: watch('customerId'),
              username: watch('username'),
              isUser: watch('types').includes(IS_USER),
              isDriver: watch('types').includes(IS_DRIVER),
              id: props.id,
              roles: watch('roles'),
            };
            const response = await EmployeeService.getDrivers(values);
            return response;
          }
          return [];
        },
      },
      {
        queryKey: ['vehiclesOptions', watch('roles')],
        queryFn: async () => {
          if (watch('username')) {
            const values = {
              customerId: watch('customerId'),
              username: watch('username'),
              isUser: watch('types').includes(IS_USER),
              isDriver: watch('types').includes(IS_DRIVER),
              id: props.id,
              roles: watch('roles'),
            };
            const response = await EmployeeService.getVehicles(values);
            return response;
          }
          return [];
        },
      },

      {
        queryKey: ['employees-detail-user', props.id, selectedCustomers],
        queryFn: async () => {
          if (selectedCustomers.length > 0 && watch('types').includes(IS_USER)) {
            const userResponse = await EmployeeService.user(props.id);
            if (userResponse) {
              const twoFactorAuth: string[] = [];
              if (userResponse.user.twoFactorAuth) {
                twoFactorAuth.push('twoFactorAuthWeb');
              }
              if (userResponse.user.twoFactorAuthMobile) {
                twoFactorAuth.push('twoFactorAuthMobile');
              }
              setValue('username', userResponse.user.username);
              setValue(
                'roles',
                userResponse.roles &&
                  (userResponse.roles.customer || userResponse.roles.salesChannel)
                  ? userResponse.roles.customer.length > 0
                    ? userResponse.roles.customer.map((i) => String(i))
                    : userResponse.roles.salesChannel.map((i) => String(i))
                  : []
              );
              setValue(
                'vehicles',
                userResponse.vehicles.map((i) => String(i))
              );
              setValue(
                'drivers',
                userResponse.drivers.map((i) => String(i))
              );
              setValue('twoFactorAuth', ['twoFactorAuthWeb', 'twoFactorAuthMobile']);
              setValue('status', userResponse.user.status);
            }

            return userResponse;
          }
          return null;
        },
        enabled: !!(selectedCustomers.length > 0 && watch('types').includes(IS_USER)), // Podmienka, aby sa dotaz vykonal
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  // Processing data from queries
  // const employeeData = results[0].data as IEmployeeData | undefined;
  // const idCardOptionsData = results[1].data as CmdSimpleSelectOption[] | undefined;
  // const workDepartmentOptionsData = results[2].data as CmdSimpleSelectOption[] | undefined;
  // const costCenterOptionsData = results[3].data as CmdSimpleSelectOption[] | undefined;
  const rolesOptionsData = results[4].data as CmdSimpleSelectOption[] | undefined;
  const driversOptionsData = results[5].data as CmdSimpleSelectOption[] | undefined;
  const vehiclesOptionsData = results[6].data as CmdSimpleSelectOption[] | undefined;
  const userData = results[7].data;

  const isLoading = results.some((result) => result.isLoading);

  const getCustomerName = (id: number): string => {
    const customer = getById(customers, id);
    if (customer) {
      return customer.name;
    }
    return '';
  };

  const getCostCenter = (id: number | null): string => {
    if (!id) {
      return '';
    }
    const costCenter = costCenterOptions.find(
      (item: CmdSimpleSelectOption) => Number(item.value) === id
    );
    if (costCenter) {
      return costCenter.item;
    }
    return '';
  };

  const getWorkDepartment = (id: number | null): string => {
    if (!id) {
      return '';
    }
    const workDepartment = workDepartmentOptions.find(
      (item: CmdSimpleSelectOption) => Number(item.value) === id
    );
    if (workDepartment) {
      return workDepartment.item;
    }
    return '';
  };

  const getIdentificationCards = (ids: string[]): string => {
    if (ids.length === 0) {
      return '';
    }
    const identCards = idCardOptions
      .filter((item: CmdSimpleSelectOption) => ids.includes(item.value))
      .map((item: CmdSimpleSelectOption) => item.item);

    return identCards.join(', ');
  };

  const getLabelFromOptions = (
    selectedValues: string[],
    options: CmdSimpleSelectOption[]
  ): string => {
    if (!options || selectedValues.length === 0) return '';

    const selectedItems = options
      .filter((option) => selectedValues.includes(String(option.value)))
      .map((option) => option.item);

    return selectedItems.join(', ');
  };

  const getTwoFactorAuthLabel = (
    twoFactorAuthWeb: boolean,
    twoFactorAuthMobile: boolean
  ): string => {
    if (!twoFactorAuthWeb && !twoFactorAuthMobile)
      // return f({ id: 'employee.field.twoFactorAuthDisabled' });
      return '';
    if (twoFactorAuthWeb && twoFactorAuthMobile)
      return `web, ${f({ id: 'employee.field.twoFactorAuthMobile' })}`;
    if (twoFactorAuthWeb) return 'web';
    if (twoFactorAuthMobile) return f({ id: 'employee.field.twoFactorAuthMobile' });
    return '';
  };

  return (
    <CmdSubpage
      id="employee-detail"
      title={f({ id: 'menu.employeeSettings' })}
      subtitle={f({ id: 'employee.detail' })}
      buttons={subpageButtons}
      loader={isLoading}
      onlineHelp={<OnlineHelp topic="edit-employee" />}
    >
      <div className="row">
        <div className="col-xxl-6">
          <CmdBox
            id="employee-basic-information"
            title={f({ id: 'employee.section.basicInformation' })}
          >
            {watch('customerId') && (
              <>
                <CmdLabel
                  title={f({ id: 'employee.field.customerId' })}
                  value={getCustomerName(Number(watch('customerId')))}
                  areLabels={true}
                />
                <CmdLabel
                  title={f({ id: 'employee.field.title' })}
                  value={watch('title')}
                  areLabels={true}
                />
                <CmdLabel
                  title={f({ id: 'employee.field.firstName' })}
                  value={watch('firstName')}
                  areLabels={true}
                />
                <CmdLabel
                  title={f({ id: 'employee.field.lastName' })}
                  value={watch('lastName')}
                  areLabels={true}
                />
                {watch('types').includes(IS_DRIVER) && (
                  <CmdLabel
                    title=""
                    value={f({ id: 'employee.field.isDriver' })}
                    areLabels={true}
                  />
                )}
                {watch('types').includes(IS_USER) && (
                  <CmdLabel title="" value={f({ id: 'employee.field.isUser' })} areLabels={true} />
                )}
              </>
            )}
          </CmdBox>

          <CmdBox
            id="employee-additional-information"
            title={f({ id: 'employee.section.additionalInformation' })}
          >
            <>
              <CmdLabel
                title={f({ id: 'employee.field.idCardId' })}
                value={getIdentificationCards(watch('identCards') || [])}
                areLabels={true}
              />
              <CmdLabel
                title={f({ id: 'employee.field.personalNumber' })}
                value={watch('personalNumber')}
                areLabels={true}
              />
              <CmdLabel
                title={f({ id: 'employee.field.workDepartmentId' })}
                value={
                  watch('workDepartmentId')
                    ? getWorkDepartment(Number(watch('workDepartmentId')))
                    : ''
                }
                areLabels={true}
              />
              <CmdLabel
                title={f({ id: 'employee.field.costCenterId' })}
                value={
                  typeof watch('costCenterId') === 'number'
                    ? getCostCenter(Number(watch('costCenterId')))
                    : ''
                }
                areLabels={true}
              />
              <CmdLabel
                title={f({ id: 'employee.field.workingHours' })}
                value={String(watch('workingHours'))}
                areLabels={true}
              />
            </>
          </CmdBox>
        </div>

        <div className="col-xxl-6">
          <CmdBox
            id="employee-contact-information"
            title={f({ id: 'employee.section.contactInformation' })}
          >
            <>
              <CmdLabel
                title={f({ id: 'employee.field.email' })}
                value={watch('email')}
                areLabels={true}
              />
              <CmdLabel
                title={f({ id: 'employee.field.phone' })}
                value={watch('phone')}
                areLabels={true}
              />
              <CmdLabel
                title={f({ id: 'employee.field.addressStreet' })}
                value={watch('addressStreet')}
                areLabels={true}
              />
              <CmdLabel
                title={f({ id: 'employee.field.addressCity' })}
                value={watch('addressCity')}
                areLabels={true}
              />
              <CmdLabel
                title={f({ id: 'employee.field.addressPostal' })}
                value={watch('addressPostal')}
                areLabels={true}
              />
            </>
          </CmdBox>

          {watch('username') && userData && (
            <CmdBox id="employee-user-data" title={f({ id: 'employee.section.userData' })}>
              <>
                <CmdLabel
                  title={f({ id: 'table_list.columns.gdprUsers.username' })}
                  value={watch('username') || ''}
                  areLabels={true}
                />
                <CmdLabel
                  title={f({ id: 'employee.field.roles' })}
                  value={getLabelFromOptions(watch('roles') || [], rolesOptionsData || [])}
                  areLabels={true}
                />
                <CmdLabel
                  title={f({ id: 'employee.field.vehicles' })}
                  value={getLabelFromOptions(watch('vehicles') || [], vehiclesOptionsData || [])}
                  areLabels={true}
                />
                <CmdLabel
                  title={f({ id: 'employee.field.drivers' })}
                  value={getLabelFromOptions(watch('drivers') || [], driversOptionsData || [])}
                  areLabels={true}
                />
                <CmdLabel
                  title={f({ id: 'employee.field.twoFactorAuth' })}
                  value={getTwoFactorAuthLabel(
                    watch('twoFactorAuth').includes('twoFactorAuthWeb'),
                    watch('twoFactorAuth').includes('twoFactorAuthMobile')
                  )}
                  areLabels={true}
                />
              </>
            </CmdBox>
          )}
        </div>
      </div>
    </CmdSubpage>
  );
}

export default Detail;
